import { reportUrl } from '@glass/types';
import { createAuth } from '~/lib/auth';
import { logger } from '~/lib/logger';
import { config } from './config';

/**
 * Typed client for the glass-classify API (Appendix B, 12.3).
 * Every request carries a fresh Entra ACCESS token; base URL comes from env
 * (same-origin in prod — the API serves the build; dev-proxy to :8080).
 */

// ---- types ----
//
// The wire types live in packages/types (@glass/types, Phase 16.5.1) so apps/ui and
// apps/review share one verdict shape. Re-exported here so no screen import changes.
export type {
  AreaVerdict,
  Claim,
  Decision,
  DecisionSource,
  GlassArea,
  ImageKind,
  ImageRole,
  JobImageOut,
  JobStatus,
  LegSummary,
  ShotType,
  StageTimestamps,
  Verdict,
} from '@glass/types';
export { SHOT_TYPES } from '@glass/types';

import type {
  Claim,
  GlassArea,
  ImageRole,
  JobImageOut,
  JobListItem,
  JobStatus,
  ShotType,
  StageTimestamps,
  Verdict,
} from '@glass/types';

/** The only journey. Normalised server-side too, so a stale value cannot split the data. */
export const JOURNEY_TYPE = 'GLASS_JOURNEY';

export interface ImageInput {
  role: ImageRole;
  glass_area?: GlassArea;
  /** Exactly one source. */
  gcs_uri?: string;
  url?: string;
  /**
   * Base64 image bytes, the native submit path (Appendix B `images[].data`). A
   * `data:image/...;base64,` prefix is tolerated; the server sniffs the content
   * type from the decoded bytes and never trusts a caller-supplied label.
   */
  data?: string;
  /**
   * Which of the area's shots this is (`wide | close | angle | scale`).
   *
   * The server has accepted it since migration 0014 and answers with `retake_shot`
   * naming which one to re-capture. Sending it is what makes that answer actionable.
   */
  shot_type?: ShotType;
}

export interface SubmitJobRequest {
  idempotency_key: string;
  claim: Claim;
  claimed_areas: string[]; // "GLASS|<AREA>" composites
  callback_url?: string;
  images: ImageInput[];
}

export interface SubmitJobResponse {
  job_id: string;
  status: JobStatus;
}

export interface SignedURLRequest {
  content_type: string;
  filename: string;
  role: ImageRole;
  glass_area?: GlassArea;
}

export interface SignedURLResponse {
  upload_url: string;
  gcs_uri: string;
  expires_at: string;
  method: 'PUT' | 'POST';
}

export interface JobStatusResponse {
  job_id: string;
  status: JobStatus;
  error?: string;
  created_at: string;
  updated_at: string;
  result?: Verdict;
  /** 0024. Absent on a job written before the migration, and per stage not yet reached. */
  stages?: StageTimestamps;
}

/*
  `JobListItem` is the SHARED type, not a local restatement of it.

  It was declared here with four fields while `@glass/types` carried eight, so 18.6's
  `decision_source`, `held_at`, `vehicle` and `claimed_areas` reached the wire and the
  cards never saw them — the screens were reading a type that had quietly stopped
  describing the response. Re-exported so no screen's import changes.
*/
export type { JobListItem, JobVehicle } from '@glass/types';

export interface JobListResponse {
  jobs: JobListItem[];
  next_cursor?: string;
}

// ---- error ----

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---- core fetch ----

/*
  One backend, built once, shared with the provider's own instance semantics: MSAL keeps a
  token cache and AppAuth keeps the keystore session, and a second instance would hold a
  second cache that disagrees about who is signed in.
*/
const auth = createAuth();

/**
 * The ACCESS token, not the ID token (18.12).
 *
 * They are different tokens with different audiences. An ID token says who signed in, to
 * THIS app; an access token says what may be called, and the API's verifier checks `aud`
 * against its own client id. Sending the ID token produces a 401 naming the audience,
 * which reads like a misconfigured API rather than the wrong token being sent.
 *
 * MSAL and AppAuth both renew silently here, so a token is fresh per request rather than
 * per session — an upload that starts before expiry and finishes after it is the case
 * that fails otherwise.
 */
async function bearerHeaders(): Promise<Record<string, string>> {
  let token: string | null = null;
  try {
    token = await auth.getAccessToken();
  } catch (e) {
    logger.error('[api] could not get an access token', e);
  }
  if (!token) {
    /*
      Say so. An absent Authorization header produces a 401 reading "provide x-api-key or
      an Entra Bearer token" — which is correct, and says nothing about the token having
      been silently unavailable on this side. That cost an afternoon once.
    */
    logger.error('[api] no access token; sending the request unauthenticated');
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

/**
 * The code the API uses for a verified token that lacks the operator role.
 *
 * Branch on the CODE, never the status or the message. The status moved from 401 to 403
 * once the API started distinguishing unauthenticated from unauthorised, and the wording
 * changed with it; the code is the part the service commits to keeping
 * (`refuseHuman` in `internal/auth/auth.go`).
 */
export const ERR_NO_ROLE = 'ERR_NO_ROLE';

type NoRoleListener = () => void;
let noRoleListener: NoRoleListener | null = null;

/**
 * Hear about a role refusal from anywhere in the app.
 *
 * `useAuth` is the only caller. The refusal arrives on whatever request happened to go
 * first, which is not something a screen can usefully catch — the answer is not "this
 * list failed", it is "this person cannot use this app", and that belongs to auth state.
 * One listener rather than a set, because there is one owner of that state by design.
 */
export function onNoRole(fn: NoRoleListener | null): void {
  noRoleListener = fn;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(await bearerHeaders()),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let code = 'ERR_UNKNOWN';
    let message = `request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      /* non-json error body */
    }
    // Before the throw: every caller of this handles its own failure, and none of them
    // can act on this one. Telling auth first means the screen changes rather than the
    // operator reading "could not load your journeys" over a home they cannot use.
    if (code === ERR_NO_ROLE) noRoleListener?.();
    throw new ApiError(res.status, code, message);
  }
  return res.json() as Promise<T>;
}

// ---- endpoint methods (Appendix B) ----

/**
 * Coerce a verdict's nullable-in-practice fields to the shapes the contract
 * documents.
 *
 * Go marshals a nil slice as `null`, not `[]`, and the verdict's
 * `blocking_reasons` has no omitempty — so a perfectly healthy job with nothing
 * blocking it arrives as `"blocking_reasons": null` while docs/api-contract.md
 * documents `blocking_reasons[]`. Reading `.length` off that crashed the app on a
 * real device the moment a job completed.
 *
 * Normalising here rather than at each use site means one place to be liberal in
 * what we accept, and component code that can rely on its types. Applied to
 * every endpoint that returns a verdict.
 */
function normalizeVerdict<T extends { result?: Verdict } | Verdict>(payload: T): T {
  const verdict = (payload as { result?: Verdict }).result ?? (payload as Verdict);
  if (!verdict || typeof verdict !== 'object' || !('decision' in verdict)) return payload;

  verdict.blocking_reasons ??= [];
  verdict.areas ??= [];
  for (const area of verdict.areas) {
    area.reasons ??= [];
    area.legs ??= [];
    // Numeric scores are rendered with toFixed, which throws on null.
    area.confidence ??= 0;
    area.judge_agreement ??= 0;
  }
  if (verdict.enrichment) verdict.enrichment.warnings ??= [];
  return payload;
}

/**
 * Coerce a job list's `jobs` to an array.
 *
 * Same root cause as normalizeVerdict: Go marshals a NIL SLICE as `null`, not `[]`, so
 * an account with no jobs at all returns `"jobs": null` while the contract documents
 * `jobs[]`. Reading `.length` off that crashed the app on launch — and it only appeared
 * once the job history was purged, because until then the list was never empty.
 *
 * Third time this class has bitten (blocking_reasons, per-area arrays, now this), so the
 * rule is: any array or number arriving from this API is normalised HERE, at the one
 * boundary, and never trusted from its TypeScript type alone.
 */
function normalizeList(payload: JobListResponse): JobListResponse {
  payload.jobs ??= [];
  return payload;
}

export const api = {
  me(): Promise<{ uid: string; email: string }> {
    return apiFetch('/v1/me');
  },

  requestUploadUrl(req: SignedURLRequest): Promise<SignedURLResponse> {
    return apiFetch<SignedURLResponse>('/v1/uploads/signed-url', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  submitJob(req: SubmitJobRequest): Promise<SubmitJobResponse> {
    return apiFetch<SubmitJobResponse>('/v1/jobs', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  /**
   * Where the assessment report actually lives, for this request.
   *
   * JSON, not the 302. The route authenticates from the `Authorization` header and a
   * browser tab or a system-browser hand-off cannot set one, so opening the stable link
   * directly returns 401. The app asks for the signed URL — which needs no header — and
   * opens that instead.
   *
   * `Accept` is explicit because the server matches the exact token `application/json`,
   * and deliberately not a wildcard: every API caller sends a wildcard or nothing, and
   * treating that as "wants JSON" would have moved the redirect contract underneath Link.
   */
  reportLink(jobId: string): Promise<{ url: string; expires_at: string }> {
    return apiFetch(reportUrl('', jobId), { headers: { Accept: 'application/json' } });
  },

  getJob(jobId: string): Promise<JobStatusResponse> {
    return apiFetch<JobStatusResponse>(`/v1/jobs/${jobId}`).then(normalizeVerdict);
  },

  /** List jobs. Always scoped to the calling identity — there is no privileged reader. */
  listJobs(limit = 25, cursor?: string, status?: string): Promise<JobListResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    if (status) params.set('status', status);
    return apiFetch<JobListResponse>(`/v1/jobs?${params}`).then(normalizeList);
  },

  getJobImages(jobId: string): Promise<{ images: JobImageOut[] }> {
    // Same nil-slice rule as normalizeList: a job whose preprocessing has not written any
    // variant yet returns `"images": null`, and JobDetailScreen reads `.length`, `.filter`
    // and `.map` off it. Every array from this API is normalised HERE, at the one
    // boundary, and never trusted from its TypeScript type alone.
    return apiFetch<{ images: JobImageOut[] }>(`/v1/jobs/${jobId}/images`).then((r) => {
      r.images ??= [];
      return r;
    });
  },

  getResult(jobId: string): Promise<Verdict> {
    return apiFetch<Verdict>(`/v1/results/${jobId}`).then(normalizeVerdict);
  },
};

// ---- upload helper (signed URL → GCS, with progress) ----

export interface UploadableFile {
  uri: string; // blob/data URL on web, file:// on native
  name: string;
  type: string; // content-type
  size?: number;
  /** Downscaled bytes, set by lib/image prepareImage. */
  base64?: string;
}

/**
 * Upload bytes to a signed GCS URL with progress. Uses XHR (fetch has no
 * upload-progress). `method` comes from the mint response (PUT prod / POST
 * fake-gcs).
 */
export async function uploadToSignedUrl(
  signed: SignedURLResponse,
  file: UploadableFile,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  // The bytes, not the URI. Sending `file.uri` uploaded the blob: URL as the
  // request body — a few dozen characters of text that GCS happily stored as
  // the "image", so the job then failed downstream on an undecodable object.
  const body = await fetch(file.uri).then((r) => r.blob());
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(signed.method, signed.upload_url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(e.loaded / e.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new ApiError(xhr.status, 'ERR_UPLOAD', `upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, 'ERR_UPLOAD', 'upload network error'));
    xhr.send(body);
  });
}
